/**
 * Model-facing encoding of runtime probe reports.
 *
 * `probeRuntime` / `probeRuntimeSequence` return rich reports for programmatic
 * callers (bench scoring, tests). Those reports repeat a FULL snapshot of every
 * watched object on every step, with float32 tails, identity rotations/scales, a
 * `worldPosition` that usually equals `position`, empty property maps and a
 * multi-sentence limitations block. A tool result enters the LLM conversation and
 * is re-read on every later turn, and the bench harness truncates any result at
 * 8,000 chars — so that padding is paid for repeatedly and, past the cap, it
 * *destroys* the evidence of the later steps. This module re-encodes a report
 * into the smallest form that still answers "did it pass, and if not, what was
 * the state when it didn't".
 *
 * Encoding rules
 * - Numbers round to 4 decimals (`-0` normalised to `0`). 1e-4 is far below any
 *   authored tolerance and is the float32 noise floor of a Babylon world matrix.
 * - A snapshot is flattened to `{path, position, world?, rotation?, scale?,
 *   properties?, timeline?, page?}`; `world` appears only when it differs from
 *   `position`, `rotation`/`scale` only when not identity, `properties` only when
 *   non-empty.
 * - `before` is the full compact snapshot list. Every later snapshot (each step's
 *   `after`, and the single-probe `after`) is a DELTA against the previous one:
 *   only objects that changed, and within them only the transform fields and
 *   property keys that changed, plus an `unchanged` count.
 * - Fields that are identical on every step (`mode`, timings, and any repeated
 *   diagnostics) hoist to a single `everyStep` block.
 * - Non-empty `graphDiagnostics`, binding diagnostics and `logs` are ALWAYS kept
 *   verbatim: they are the failure evidence. Only empty ones are dropped.
 *
 * Why a delta cannot hide a failure
 * The question a delta invites is "what about an object that SHOULD have moved
 * and did not?" — it is simply absent from the delta. That is unambiguous here
 * for three reasons: `before` states every watched object in full, so the model
 * knows the complete set and its starting values; `changed` + `unchanged` sums to
 * that same set on every step, so a missing object is provably unchanged rather
 * than omitted for space; and deltas chain from `before` with no gaps, so the
 * state after step N is exactly `before` with steps 0..N applied. A value that
 * changes and changes back within one step's frames is not visible in the delta —
 * but it was not visible in the rich `after` snapshot either, and the `moving` /
 * `changed` path lists (kept whenever non-empty) are what report it. Nothing
 * that distinguishes a passing run from a failing one is encoded implicitly:
 * check results, non-empty diagnostics, logs, `status` and `error` all survive
 * verbatim or in a directly readable form.
 */

/** Structural shape of `runtimeSnapshot()` output — also what JSON.parse yields. */
export interface SnapshotEntryLike {
    path: string;
    transform: {
        position: number[]; worldPosition: number[]; rotation: number[]; scale: number[];
        /** Absent in reports recorded before it existed. */
        slopeDeg?: { x: number; z: number };
    };
    properties: Record<string, unknown>;
    timeline?: { time: number; playing: boolean };
    /** A Pages deck's showing page, 0-based. */
    page?: { index: number; count: number };
}

export interface CheckLike {
    object: string; property: string;
    expected: unknown; actual: unknown; tolerance: number; passed: boolean;
}

/** Structural shape of a `probeRuntime()` report. */
export interface ProbeReportLike {
    mode: string; requestedMs: number; elapsedMs: number; frames: number;
    graphDiagnostics: readonly unknown[];
    before: SnapshotEntryLike[]; after: SnapshotEntryLike[];
    movingObjects: readonly string[]; changedObjects: readonly string[];
    bindingDiagnosticsBefore: readonly unknown[]; bindingDiagnosticsAfter: readonly unknown[];
    logs: readonly unknown[];
    limitations: readonly string[];
}

export interface SequenceStepLike {
    index: number; select?: string; activation?: string; checks: CheckLike[];
    mode: string; requestedMs: number; elapsedMs: number; frames: number;
    graphDiagnostics: readonly unknown[]; after: SnapshotEntryLike[];
    movingObjects: readonly string[]; changedObjects: readonly string[];
    bindingDiagnosticsAfter: readonly unknown[]; logs: readonly unknown[];
}

/** Structural shape of a `probeRuntimeSequence()` report. */
export interface SequenceReportLike {
    status: string; before: SnapshotEntryLike[]; steps: SequenceStepLike[];
    error?: string; limitations: readonly string[];
}

export interface CompactSnapshot {
    path: string;
    position?: number[]; world?: number[]; rotation?: number[]; scale?: number[];
    /** Degrees the local +X / +Z end sits above world horizontal; omitted when level. */
    slope?: { x: number; z: number };
    properties?: Record<string, unknown>; propertiesRemoved?: string[];
    timeline?: { time: number; playing: boolean } | null;
    page?: { index: number; count: number };
}

export interface CompactDelta { changed: CompactSnapshot[]; unchanged: number }

export interface CompactChecks {
    passed?: string[];
    failed?: { object: string; property: string; expected: unknown; actual: unknown; tolerance?: number }[];
}

const PROBE_NOTE = 'Native frame-driven state only; motion alone does not prove mechanical correctness or real pointer reachability.';
const SEQUENCE_NOTE = 'Synthetic selects bypass ray-picking; only the supplied scalar property expectations are asserted.';
/** The synthetic-tap acknowledgement carries no information the `select` field lacks. */
const PLAIN_ACTIVATION = /^Fired (?:tap \(down\+up\)|select-down \(held\)) on "[^"]*"\.$/;
/** Below the float32 noise of a world matrix and any tolerance a model would author. */
const EPSILON = 1e-4;
/** Slopes are reported to 0.1°; anything under this reads as level. */
const SLOPE_EPSILON_DEG = 0.05;

function roundSlope(value: number): number {
    const rounded = Math.round(value * 10) / 10;
    return Object.is(rounded, -0) ? 0 : rounded;
}

function round(value: number): number {
    if (!Number.isFinite(value)) return value;
    const rounded = Math.round(value * 1e4) / 1e4;
    return Object.is(rounded, -0) ? 0 : rounded;
}

function roundAll(values: readonly number[]): number[] {
    return values.map(round);
}

/** Property values are usually scalars but may be vectors or colour objects. */
export function roundValue(value: unknown): unknown {
    if (typeof value === 'number') return round(value);
    if (Array.isArray(value)) return (value as unknown[]).map(roundValue);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, roundValue(item)]));
    }
    return value;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function isIdentityRotation(rotation: readonly number[]): boolean {
    return rotation.length === 4
        ? same(rotation, [0, 0, 0, 1])
        : rotation.every(component => component === 0);
}

function identityRotation(arity: number): number[] {
    return arity === 4 ? [0, 0, 0, 1] : [0, 0, 0];
}

/** Full transform of a snapshot, rounded; `world` dropped when it tracks `position`. */
type TransformFields = { position: number[]; world?: number[]; rotation?: number[]; scale?: number[]; slope?: { x: number; z: number } };

function transformFields(entry: SnapshotEntryLike): TransformFields {
    const { position, worldPosition, rotation, scale, slopeDeg } = entry.transform;
    const fields: TransformFields = { position: roundAll(position) };
    if (worldPosition.some((component, index) => Math.abs(component - (position[index] ?? 0)) > EPSILON)) fields.world = roundAll(worldPosition);
    const roundedRotation = roundAll(rotation);
    if (!isIdentityRotation(roundedRotation)) fields.rotation = roundedRotation;
    const roundedScale = roundAll(scale);
    if (!same(roundedScale, [1, 1, 1])) fields.scale = roundedScale;
    if (slopeDeg && (Math.abs(slopeDeg.x) >= SLOPE_EPSILON_DEG || Math.abs(slopeDeg.z) >= SLOPE_EPSILON_DEG)) {
        fields.slope = { x: roundSlope(slopeDeg.x), z: roundSlope(slopeDeg.z) };
    }
    return fields;
}

function roundProperties(properties: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(properties).map(([key, value]) => [key, roundValue(value)]));
}

/** One snapshot in full: what `before` reports, and what a newly seen object reports. */
export function compactSnapshot(entry: SnapshotEntryLike): CompactSnapshot {
    const compact: CompactSnapshot = { path: entry.path, ...transformFields(entry) };
    const properties = roundProperties(entry.properties);
    if (Object.keys(properties).length > 0) compact.properties = properties;
    if (entry.timeline) compact.timeline = { time: round(entry.timeline.time), playing: entry.timeline.playing };
    if (entry.page) compact.page = entry.page;
    return compact;
}

export function compactSnapshots(entries: SnapshotEntryLike[]): CompactSnapshot[] {
    return entries.map(compactSnapshot);
}

/**
 * One object's change since `previous`. A field that was reported before and is
 * back at its default now is emitted with its literal current value rather than
 * silently omitted, so "returned to identity" never reads as "unchanged".
 */
function deltaEntry(previous: SnapshotEntryLike, current: SnapshotEntryLike): CompactSnapshot | null {
    const before = transformFields(previous), after = transformFields(current);
    const delta: CompactSnapshot = { path: current.path };
    if (!same(before.position, after.position)) delta.position = after.position;
    if (!same(before.world, after.world)) delta.world = after.world ?? after.position;
    if (!same(before.rotation, after.rotation)) delta.rotation = after.rotation ?? identityRotation(current.transform.rotation.length);
    if (!same(before.scale, after.scale)) delta.scale = after.scale ?? [1, 1, 1];
    if (!same(before.slope, after.slope)) delta.slope = after.slope ?? { x: 0, z: 0 };

    const beforeProperties = roundProperties(previous.properties), afterProperties = roundProperties(current.properties);
    const changedProperties: Record<string, unknown> = {}, removedProperties: string[] = [];
    for (const key of new Set([...Object.keys(beforeProperties), ...Object.keys(afterProperties)])) {
        if (same(beforeProperties[key], afterProperties[key])) continue;
        if (key in afterProperties) changedProperties[key] = afterProperties[key];
        else removedProperties.push(key);
    }
    if (Object.keys(changedProperties).length > 0) delta.properties = changedProperties;
    if (removedProperties.length > 0) delta.propertiesRemoved = removedProperties;

    const beforeTimeline = previous.timeline && { time: round(previous.timeline.time), playing: previous.timeline.playing };
    const afterTimeline = current.timeline && { time: round(current.timeline.time), playing: current.timeline.playing };
    if (!same(beforeTimeline, afterTimeline)) delta.timeline = afterTimeline ?? null;
    if (current.page && !same(previous.page, current.page)) delta.page = current.page;

    return Object.keys(delta).length > 1 ? delta : null;
}

/** Objects that changed since `previous`, plus how many did not. The two sum to the watched set. */
export function snapshotDelta(previous: SnapshotEntryLike[], current: SnapshotEntryLike[]): CompactDelta {
    const byPath = new Map(previous.map(entry => [entry.path, entry]));
    const changed: CompactSnapshot[] = [];
    let unchanged = 0;
    for (const [index, entry] of current.entries()) {
        const aligned = previous[index]?.path === entry.path ? previous[index] : byPath.get(entry.path);
        const delta = aligned ? deltaEntry(aligned, entry) : compactSnapshot(entry);
        if (delta) changed.push(delta);
        else unchanged++;
    }
    return { changed, unchanged };
}

const leafName = (path: string): string => path.split('/').pop() ?? path;

/** `passed` collapses to one readable token per check; `failed` keeps the full record. */
export function compactChecks(checks: readonly CheckLike[]): CompactChecks | undefined {
    const compact: CompactChecks = {};
    const passed = checks.filter(check => check.passed)
        .map(check => `${leafName(check.object)}.${check.property}=${JSON.stringify(roundValue(check.actual)) ?? 'undefined'}`);
    const failed = checks.filter(check => !check.passed).map(check => {
        const entry: { object: string; property: string; expected: unknown; actual: unknown; tolerance?: number } = {
            object: check.object, property: check.property,
            expected: roundValue(check.expected), actual: roundValue(check.actual),
        };
        if (check.tolerance) entry.tolerance = round(check.tolerance);
        return entry;
    });
    if (passed.length > 0) compact.passed = passed;
    if (failed.length > 0) compact.failed = failed;
    return passed.length > 0 || failed.length > 0 ? compact : undefined;
}

function keepList(key: string, values: readonly unknown[] | undefined): Record<string, unknown> {
    return values && values.length > 0 ? { [key]: values } : {};
}

function keepActivation(activation: string | undefined): Record<string, unknown> {
    if (!activation) return {};
    const [head, ...notes] = activation.split('\n');
    const kept = PLAIN_ACTIVATION.test(head) ? notes : [head, ...notes];
    return kept.length > 0 ? { activation: kept.join('\n') } : {};
}

/** Timings a step shares with its request add nothing; `requestedMs` only differs when a frame budget was cut short. */
function timing(report: { mode: string; requestedMs: number; elapsedMs: number; frames: number }): Record<string, unknown> {
    return {
        mode: report.mode,
        ...(report.requestedMs === report.elapsedMs ? {} : { requestedMs: report.requestedMs }),
        elapsedMs: report.elapsedMs, frames: report.frames,
    };
}

/** Keys worth hoisting out of every step when all steps agree on them. */
const HOISTABLE = ['mode', 'requestedMs', 'elapsedMs', 'frames', 'graphDiagnostics', 'bindingDiagnosticsAfter', 'logs'] as const;

export function compactProbeReport(report: ProbeReportLike): Record<string, unknown> {
    return {
        ...timing(report),
        before: compactSnapshots(report.before),
        after: snapshotDelta(report.before, report.after),
        ...keepList('movingObjects', report.movingObjects),
        ...keepList('changedObjects', report.changedObjects),
        ...keepList('graphDiagnostics', report.graphDiagnostics),
        ...keepList('bindingDiagnosticsBefore', report.bindingDiagnosticsBefore),
        ...keepList('bindingDiagnosticsAfter', report.bindingDiagnosticsAfter),
        ...keepList('logs', report.logs),
        limitations: PROBE_NOTE,
    };
}

export function compactSequenceReport(report: SequenceReportLike): Record<string, unknown> {
    let previous = report.before;
    const steps: Record<string, unknown>[] = report.steps.map(step => {
        const after = snapshotDelta(previous, step.after);
        previous = step.after;
        const checks = compactChecks(step.checks);
        return {
            ...(step.select === undefined ? {} : { select: step.select }),
            ...keepActivation(step.activation),
            ...(checks ? { checks } : {}),
            ...timing(step),
            after,
            ...keepList('movingObjects', step.movingObjects),
            ...keepList('changedObjects', step.changedObjects),
            ...keepList('graphDiagnostics', step.graphDiagnostics),
            ...keepList('bindingDiagnosticsAfter', step.bindingDiagnosticsAfter),
            ...keepList('logs', step.logs),
        };
    });

    // Hoist whatever every step agrees on; with one step there is nothing to fold.
    const everyStep: Record<string, unknown> = {};
    if (steps.length > 1) {
        for (const key of HOISTABLE) {
            if (!steps.every(step => key in step && same(step[key], steps[0][key]))) continue;
            everyStep[key] = steps[0][key];
            for (const step of steps) delete step[key];
        }
    }

    return {
        status: report.status,
        ...(report.error === undefined ? {} : { error: report.error }),
        ...(Object.keys(everyStep).length > 0 ? { everyStep } : {}),
        before: compactSnapshots(report.before),
        steps,
        limitations: SEQUENCE_NOTE,
    };
}

const isSequence = (report: ProbeReportLike | SequenceReportLike): report is SequenceReportLike =>
    Array.isArray((report as SequenceReportLike).steps);

/** The tool-boundary encoder: rich report in, model-facing JSON object out. */
export function compactRuntimeReport(report: ProbeReportLike | SequenceReportLike): Record<string, unknown> {
    return isSequence(report) ? compactSequenceReport(report) : compactProbeReport(report);
}
